import { Flame } from "lucide-react";
import { BRAND } from "@/lib/constants";

/**
 * Phoenix of the Detroit Fire Department logo.
 *
 * When BRAND.logoUrl is set (official logo file uploaded), renders the real image.
 * Otherwise renders a clearly-marked placeholder slot — NEVER a generic fire logo
 * masquerading as the official Phoenix logo.
 *
 * Props:
 *   - size: "sm" | "md" | "lg" — controls placeholder/logo sizing
 *   - showPlaceholderText: boolean — visually note "Official logo pending"
 */
export default function Logo({ size = "md", showPlaceholderText = false }) {
  const dimensions = {
    sm: "h-9 w-9",
    md: "h-11 w-11",
    lg: "h-12 w-12 lg:h-14 lg:w-14",
  }[size];

  if (BRAND.logoUrl) {
    return (
      <img
        src={BRAND.logoUrl}
        alt={BRAND.logoAlt}
        className={`${dimensions} object-contain shrink-0`}
        data-testid="brand-logo"
      />
    );
  }

  // Placeholder slot — NOT a generic fire logo claiming to be Phoenix's official mark
  return (
    <span
      className={`${dimensions} relative inline-flex items-center justify-center rounded-full bg-gradient-to-br from-amber-600 to-red-700 ring-1 ring-amber-500/40 shadow-[0_8px_30px_-8px_rgba(217,119,6,0.6)]`}
      role="img"
      aria-label="Official Phoenix logo pending upload"
      title="Official Phoenix logo pending upload"
      data-testid="brand-logo-placeholder"
      data-placeholder="official-phoenix-logo-pending-upload"
    >
      <Flame className="h-1/2 w-1/2 text-black" strokeWidth={2.5} />
      {showPlaceholderText && (
        <span className="absolute -bottom-5 left-1/2 -translate-x-1/2 whitespace-nowrap text-[9px] tracking-[0.18em] uppercase text-slate-500 font-accent">
          Official logo pending
        </span>
      )}
    </span>
  );
}
